/**
 * "Was ist dir wichtig?" — the six default intents that drive the homepage
 * comparison. Everything else (search, issuer, rates) lives behind the
 * advanced filters.
 */
import { getCardCategory } from "@/lib/cardCategory";

export type IntentKey =
  | "kostenlos"
  | "reisen"
  | "versicherungen"
  | "mietwagen"
  | "echte-kreditkarte"
  | "kein-girokonto";


export interface IntentDefinition {
  key: IntentKey;
  label: string;
  description: string;
  /** Related landing page for internal linking (optional). */
  href?: string;
  match: (card: any) => boolean;
}

const fxIntl = (card: any): number | null => {
  const value = card?.fx_fee_intl_percent ?? card?.foreign_tx_fee_percent;
  return typeof value === "number" ? value : null;
};

const hasTravelBenefit = (card: any) => fxIntl(card) === 0;

const hasInsurance = (card: any) =>
  card?.travel_insurance === true ||
  card?.travel_health_insurance === true ||
  card?.travel_cancellation_insurance === true ||
  card?.travel_baggage_insurance === true ||
  card?.travel_accident_insurance === true ||
  card?.rental_car_insurance === true;

export const INTENTS: IntentDefinition[] = [
  {
    key: "kostenlos",
    label: "Kostenlos",
    description: "Keine Jahresgebühr",
    href: "/kostenlose-kreditkarte",
    match: (card) => (card?.annual_fee ?? 0) === 0,
  },
  {
    key: "reisen",
    label: "Reisen",
    description: "Keine Fremdwährungsgebühr",
    href: "/reisekreditkarte",
    match: hasTravelBenefit,
  },
  {
    key: "versicherungen",
    label: "Versicherungen",
    description: "Schutz inklusive",
    match: hasInsurance,
  },
  {
    key: "mietwagen",
    label: "Mietwagen & Kaution",
    description: "Echte Kreditkarte mit Vorteilen",
    match: (card) => {
      const category = getCardCategory(card);
      return (category === "revolving" || category === "charge") && card?.rental_car_insurance === true;
    },
  },
  {

    key: "echte-kreditkarte",
    label: "Echte Kreditkarte",
    description: "Mit Kreditrahmen",
    href: "/kreditkarten",
    match: (card) => {
      const category = getCardCategory(card);
      return category === "revolving" || category === "charge";
    },
  },
  {
    key: "kein-girokonto",
    label: "Kein neues Girokonto",
    description: "Nur die Karte",
    href: "/kreditkarte-ohne-girokonto",
    match: (card) => card?.current_account_included !== true,
  },
];

export const INTENT_KEYS = INTENTS.map((i) => i.key);

export function isIntentKey(value: string): value is IntentKey {
  return (INTENT_KEYS as string[]).includes(value);
}

/** A card matches the selection when it satisfies every chosen intent. */
export function matchesIntents(card: any, selected: IntentKey[]): boolean {
  if (selected.length === 0) return true;
  return selected.every((key) => {
    const intent = INTENTS.find((i) => i.key === key);
    return intent ? intent.match(card) : true;
  });
}

/** How many of the chosen intents a card satisfies — used for ranking. */
export function intentScore(card: any, selected: IntentKey[]): number {
  return selected.reduce((sum, key) => {
    const intent = INTENTS.find((i) => i.key === key);
    return sum + (intent && intent.match(card) ? 1 : 0);
  }, 0);
}

export function parseIntents(raw: string | null | undefined): IntentKey[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((v) => v.trim())
    .filter(isIntentKey);
}

export function serializeIntents(selected: IntentKey[]): string {
  return selected.join(",");
}
